import CaptainResponseAPI from 'dashboard/api/captain/response';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import { createStore } from '../storeFactory';
import {
  createRecord,
  updateRecord,
  deleteRecord,
} from '../storeFactoryHelper';

const SET_PENDING_COUNT = 'SET_PENDING_COUNT';
const START_RESPONSE_REQUEST = 'START_RESPONSE_REQUEST';

const beginRequest = (context, operation, assistantId) => {
  const accountId = context.rootGetters?.getCurrentAccountId;
  const routeAssistant = context.rootState?.route?.params?.assistantId;
  const key = JSON.stringify([accountId, assistantId ?? null]);
  context.commit(START_RESPONSE_REQUEST, { operation, key });
  const requestId = context.state.responseRequests[operation];
  return () =>
    context.state.responseRequests[operation] === requestId &&
    context.rootGetters?.getCurrentAccountId === accountId &&
    context.rootState?.route?.params?.assistantId === routeAssistant;
};

const contextualWrite =
  (action, operation, mutations) => async (context, payload) => {
    const assistantId = context.rootState?.route?.params?.assistantId;
    const isCurrent = beginRequest(context, operation, assistantId);
    const listRequestId = context.state.responseRequests.list;
    return action(
      {
        ...context,
        commit: (type, value) => {
          if (!isCurrent()) return;
          if (
            type !== mutations.SET_UI_FLAG &&
            context.state.responseRequests.list !== listRequestId
          )
            return;
          context.commit(type, value);
        },
      },
      payload
    );
  };

export default createStore({
  name: 'CaptainResponse',
  API: CaptainResponseAPI,
  getters: {
    getPendingCount: state => state.meta.pendingCount || 0,
  },
  mutations: {
    [START_RESPONSE_REQUEST](state, { operation, key }) {
      state.responseRequests = {
        ...state.responseRequests,
        [operation]: (state.responseRequests?.[operation] || 0) + 1,
      };
      if (state.responseContexts?.[operation] !== key) {
        if (operation === 'list') {
          state.records = [];
          state.meta = {};
          state.uiFlags = {
            ...state.uiFlags,
            creatingItem: false,
            updatingItem: false,
            deletingItem: false,
          };
        } else if (operation === 'pending') {
          state.meta = { ...state.meta, pendingCount: 0 };
        }
      }
      state.responseContexts = { ...state.responseContexts, [operation]: key };
    },
    [SET_PENDING_COUNT](state, count) {
      state.meta = {
        ...state.meta,
        pendingCount: Number(count),
      };
    },
  },
  actions: mutations => ({
    create: contextualWrite(
      createRecord(mutations, CaptainResponseAPI),
      'create',
      mutations
    ),
    update: contextualWrite(
      updateRecord(mutations, CaptainResponseAPI),
      'update',
      mutations
    ),
    delete: contextualWrite(
      deleteRecord(mutations, CaptainResponseAPI),
      'delete',
      mutations
    ),
    get: async (context, params = {}) => {
      const isCurrent = beginRequest(context, 'list', params.assistantId);
      context.commit(mutations.SET_UI_FLAG, { fetchingList: true });
      try {
        const response = await CaptainResponseAPI.get(params);
        if (!isCurrent()) return undefined;
        context.commit(mutations.SET, response.data.payload);
        context.commit(mutations.SET_META, response.data.meta);
        return response.data.payload;
      } catch (error) {
        if (isCurrent()) return throwErrorMessage(error);
        return undefined;
      } finally {
        if (isCurrent())
          context.commit(mutations.SET_UI_FLAG, { fetchingList: false });
      }
    },
    removeBulkResponses: ({ commit, state }, ids) => {
      const updatedRecords = state.records.filter(
        record => !ids.includes(record.id)
      );
      commit(mutations.SET, updatedRecords);
    },
    updateBulkResponses: ({ commit, state }, approvedResponses) => {
      // Create a map of updated responses for faster lookup
      const updatedResponsesMap = approvedResponses.reduce((map, response) => {
        map[response.id] = response;
        return map;
      }, {});

      // Update existing records with updated data
      const updatedRecords = state.records.map(record => {
        if (updatedResponsesMap[record.id]) {
          return updatedResponsesMap[record.id]; // Replace with the updated response
        }
        return record;
      });

      commit(mutations.SET, updatedRecords);
    },
    fetchPendingCount: async (context, assistantId) => {
      const isCurrent = beginRequest(context, 'pending', assistantId);
      try {
        const response = await CaptainResponseAPI.get({
          status: 'pending',
          page: 1,
          assistantId,
        });
        const count = response.data?.meta?.total_count || 0;
        if (isCurrent()) context.commit(SET_PENDING_COUNT, count);
      } catch (error) {
        if (isCurrent()) context.commit(SET_PENDING_COUNT, 0);
      }
    },
  }),
});
